import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fetchUsers } from '../redux/actions/userActions';

type RouteMiddlewareProps = {
  children: React.ReactNode;
  requireAuth?: boolean;
};

const RouteMiddleware = ({ children, requireAuth = true }: RouteMiddlewareProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const usersState = useAppSelector(state => state.users);
  const [isInitializing, setIsInitializing] = useState(true);
  const [authStatus, setAuthStatus] = useState<boolean | null>(null);
  const previousAuthStatus = useRef<boolean | null>(null);
  const isDataLoading = useRef(false);
  const lastRedirectTime = useRef(0);

  // Define all possible token names we want to check for
  const tokenNames = ['pms_admin_', 'pms_employee_'];

  // Check if any of the auth tokens exist in cookies
  const checkForTokens = () => {
    if (!document.cookie) return false;

    const cookies = document.cookie.split('; ');
    for (const tokenName of tokenNames) {
      for (const cookie of cookies) {
        if (cookie.startsWith(`${tokenName}=`)) {
          console.log(`Found token: ${tokenName}`);
          return true;
        }
      }
    }
    return false;
  };

  // Public routes that don't require authentication
  const publicRoutes = ['/auth/login', '/auth/register'];
  const isPublicRoute = publicRoutes.includes(location.pathname);

  // Check for token changes - run on every render to catch new tokens
  useEffect(() => {
    const currentAuthStatus = checkForTokens();

    // If auth status has changed, update state
    if (currentAuthStatus !== authStatus) {
      setAuthStatus(currentAuthStatus);
    }

    // Initialize on first run
    if (isInitializing && authStatus === null) {
      setAuthStatus(currentAuthStatus);

      // Add a small delay to prevent rapid state changes
      const timer = setTimeout(() => {
        setIsInitializing(false);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [location.pathname, document.cookie]);

  // Helper function to prevent too many redirects
  const safeRedirect = (path: string) => {
    const now = Date.now();
    // Only redirect if it's been at least 500ms since the last redirect
    if (now - lastRedirectTime.current > 500) {
      lastRedirectTime.current = now;
      navigate(path, { replace: true });
    }
  };

  // Handle redirect when authentication state changes
  useEffect(() => {
    // Skip during initialization
    if (isInitializing) return;

    const prevStatus = previousAuthStatus.current;
    previousAuthStatus.current = authStatus;

    // If we've just become authenticated and we're on a public route
    if (authStatus && !prevStatus && isPublicRoute) {
      console.log('Just logged in, redirecting to dashboard');
      safeRedirect('/dashboard');
    }
  }, [authStatus, isPublicRoute, navigate, isInitializing]);

  // For debugging purposes - only log when auth status changes
  useEffect(() => {
    if (authStatus !== null) {
      console.log('Location pathname:', location.pathname);
      console.log('Has auth token:', authStatus);
      console.log('Is public route:', isPublicRoute);
      console.log('All cookies:', document.cookie);
    }
  }, [location.pathname, authStatus, isPublicRoute]);

  // Load user data when the user is authenticated
  useEffect(() => {
    if (authStatus && requireAuth && !isDataLoading.current && !usersState.hasFetched && !usersState.loading) {
      console.log('RouteMiddleware: User is authenticated and user data has not been fetched yet');
      
      console.log('RouteMiddleware: Loading user data');
      isDataLoading.current = true;
      
      dispatch(fetchUsers())
        .unwrap()
        .then(userData => {
          console.log('RouteMiddleware: User data loaded successfully');
          isDataLoading.current = false;
        })
        .catch(error => {
          console.error('RouteMiddleware: Failed to load user data:', error);
          isDataLoading.current = false;
        });
    }
  }, [authStatus, dispatch, requireAuth, usersState.hasFetched, usersState.loading]);

  // Show nothing while we're determining the auth status
  if (isInitializing) {
    return null;
  }

  // Handle root path redirect
  if (location.pathname === '/') {
    return <Navigate to="/dashboard" replace />;
  }

  // Handle authentication logic
  if (requireAuth) {
    if (!authStatus) {
      console.log('No token found, redirecting to login');
      return <Navigate to="/auth/login" state={{ from: location }} replace />;
    }
  } else {
    if (authStatus && isPublicRoute) {
      console.log(
        'User is authenticated and trying to access public route, redirecting to dashboard'
      );
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
};

export default RouteMiddleware;
